import ActivateMembershipType from 'src/features/membershipType/components/forms/ActivateMembershipType';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import DeactivateMembershipType from 'src/features/membershipType/components/forms/DeactivateMembershipType';
import CopyMembershipTypeFormModal from 'src/features/membershipType/components/forms/CopyMembershipTypeFormModal';
import { generalMessages } from 'src/registry/utils/messages';
import UpdateMembershipTypeFormModal from 'src/features/membershipType/components/forms/UpdateMembershipTypeFormModal';
import { commonMessages } from 'src/utils/messages';
import DeleteMembershipType from 'src/features/membershipType/components/forms/DeleteMembershipType';
import { useOnError } from 'src/utils/errors';
import { useDeleteMembershipType } from 'src/features/membershipType/api/useDeleteMembershipType';
import { queryClient } from 'src/lib/react-query';
import { LIST_MEMBERSHIP_TYPES_RESOURCE } from 'src/features/membershipType/api/useListMembershipTypes';
import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { MembershipType } from 'src/features/membershipType/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

type MembershipTypeActionButtonsProps = {
    membershipTemplate: MembershipType;
    isSystemMembershipTemplate: boolean;
    isActiveMembershipTemplate: boolean;
};

const MembershipTypeActionButtons = ({
    membershipTemplate,
    isActiveMembershipTemplate,
    isSystemMembershipTemplate,
}: MembershipTypeActionButtonsProps) => {
    const { formatMessage, locale } = useIntl();
    const navigate = useNavigate();
    const onError = useOnError();

    const { mutate: deleteTemplate } = useDeleteMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPES_RESOURCE] });
            void navigate(`/${PATH.ADMINISTRATION}/membership-templates`);
            toast({
                title: formatMessage(membershipTypeMessages.deleteTemplateSuccess),
                description: formatMessage(membershipTypeMessages.deleteTemplateSuccessDescription, {
                    entry: membershipTemplate?.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <div className="flex flex-row gap-2">
            {!membershipTemplate.is_active && !isSystemMembershipTemplate && (
                <ActivateMembershipType selectedTemplate={membershipTemplate}>
                    <Button size="mdIcon" disabled={!membershipTemplate?.can_be_activated}>
                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                        <span className="hidden lg:block">
                            {formatMessage(membershipTypeMessages.activateTemplate)}
                        </span>
                    </Button>
                </ActivateMembershipType>
            )}
            {membershipTemplate.is_active && !isSystemMembershipTemplate && (
                <DeactivateMembershipType selectedTemplate={membershipTemplate}>
                    <Button size="mdIcon" variant="destructiveLight">
                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                        <span className="hidden lg:block">
                            {formatMessage(membershipTypeMessages.deactivateTemplate)}
                        </span>
                    </Button>
                </DeactivateMembershipType>
            )}

            <CopyMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                <Button variant="default" size="mdIcon" className="cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.COPY} />
                    <span className="hidden lg:block">{formatMessage(generalMessages.copy)}</span>
                </Button>
            </CopyMembershipTypeFormModal>

            <TooltipProvider>
                {!isSystemMembershipTemplate && (
                    <>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-block">
                                    <UpdateMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                                        <Button size="mdIcon" variant="outline" disabled={isActiveMembershipTemplate}>
                                            <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                            <span className="hidden lg:block">
                                                {formatMessage(commonMessages.edit)}
                                            </span>
                                        </Button>
                                    </UpdateMembershipTypeFormModal>
                                </span>
                            </TooltipTrigger>
                            {isActiveMembershipTemplate && (
                                <TooltipContent side="top" className="max-w-56">
                                    <FormattedMessage {...membershipTypeMessages.activeMembershipTypeProtectedError} />
                                </TooltipContent>
                            )}
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-block">
                                    <DeleteMembershipType
                                        selectedTemplate={membershipTemplate}
                                        deleteTemplate={deleteTemplate}
                                    >
                                        <Button
                                            size="mdIcon"
                                            variant="destructiveLight"
                                            disabled={isActiveMembershipTemplate}
                                        >
                                            <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                            <span className="hidden lg:block">
                                                {formatMessage(generalMessages.delete)}
                                            </span>
                                        </Button>
                                    </DeleteMembershipType>
                                </span>
                            </TooltipTrigger>
                            {isActiveMembershipTemplate && (
                                <TooltipContent side="top" className="max-w-56">
                                    <FormattedMessage {...membershipTypeMessages.activeMembershipTypeProtectedError} />
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
};

export default MembershipTypeActionButtons;
